import { useWindowScroll } from '@vueuse/core';

/** Compute an element's visibility when scrolling the page - hiding it when scrolling enough down, and showing again when scrolling up. */
export default function useScrollHiding({
    bottomThreshold,
    topThreshold,
    elementHeight = bottomThreshold,
    enablePoint,
}: {
    /** Minimum amount (pixels) to scroll down before hiding the element. This should be at least the height of the element to be controlled! */
    bottomThreshold: number
    /** Minimum amount (pixels) to scroll up before re-showing previously hidden element. This should be at least the height of the element to be controlled! */
    topThreshold: number
    /** Minimum position (pixels) from the top of the page before the hiding behavior activates. */
    // NOTE: Couldn't get this to work entirely nicely at least if we were using two separate hiding elements with different thresholds on the same page. Check before using.
    enablePoint?: number
    /** Controllable element height. Only needed if `enablePoint` is set. This should be as accurate as possible to avoid issues. */
    elementHeight?: number
}) {
    const { y } = useWindowScroll();

    const biggestY = ref(y.value);
    const smallestY = ref(y.value);
    const isVisible = ref(true);
    const yBeforeHiding = ref(y.value);

    // If scrolling down and the element is showing, check the topmost scroll position we've last been at - if we've now scrolled down more than the required threshold, we should hide the element. The same goes inverse for scrolling upwards.
    watch(y, (value) => {
        // The second condition: If the element is currently showing, any scroll position we enter should be considered the bottommost point of the page we've been at. That way when the element hides after the user has scrolled up enough, our reference bottommost scroll point will be set to the point where the element got hidden, and if the user scrolls down again, the element will be visible after the user scrolls the required threshold down from there.
        if (value > biggestY.value || isVisible.value) {
            biggestY.value = value;
        }
        if (value < smallestY.value || !isVisible.value) {
            smallestY.value = value;
        }

        // Keep track of this for later use
        if (isVisible.value) {
            yBeforeHiding.value = value;
        }

        // Always show the element if our scroll position is less than the enable point - however, there's also the issue that when the element hides, it will also reduce our scroll position by about as much as its height! We counter that by requiring the new scroll position to be less than the position before hiding by more than the element's height - that means we've actually scrolled upwards at least a bit instead of the element hiding just reducing our scroll position.
        if (enablePoint !== undefined && value < enablePoint
            && (isVisible.value || yBeforeHiding.value - value > elementHeight)) {
            isVisible.value = true;
        // If scrolling back enough above the lowest scroll point we've reached, we re-show the element
        } else if (value < biggestY.value - topThreshold) {
            isVisible.value = true;
        // If scrolling enough below the highest scroll point we've previously been at, hide the element
        } else if (value > smallestY.value + bottomThreshold) {
            isVisible.value = false;
        }
    });

    return { isVisible };
}
